import { useState } from "react";
import { useSearchParams, useNavigate, Link } from "react-router-dom";
import { motion } from "framer-motion";
import { api, formatApiErrorDetail } from "@/lib/api";
import { toast } from "sonner";
import { PasswordStrength } from "@/components/PasswordStrength";

export default function ResetPassword() {
  const [params] = useSearchParams();
  const token = params.get("token");
  const [password, setPassword] = useState("");
  const [confirm, setConfirm] = useState("");
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false);
  const nav = useNavigate();

  const field = "w-full bg-transparent border-b border-white/15 py-3 text-lg focus:border-wine outline-none transition-colors placeholder:text-text-muted/50";

  const submit = async (e) => {
    e.preventDefault();
    setError("");
    if (password !== confirm) { setError("Passwords don't match"); return; }
    setLoading(true);
    try {
      await api.post("/auth/reset-password", { token, password });
      toast.success("Password updated — please sign in");
      nav("/auth");
    } catch (e2) {
      setError(formatApiErrorDetail(e2.response?.data?.detail) || e2.message);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen flex items-center justify-center px-6 pt-16">
      <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.7 }} className="w-full max-w-sm">
        <p className="text-xs uppercase tracking-[0.3em] text-gold mb-4">Recover access</p>
        <h1 className="font-display text-5xl font-light mb-10">Set a new password</h1>
        {!token ? (
          <p className="text-sm text-red-400" data-testid="reset-notoken">This reset link is missing its token. Request a new one.</p>
        ) : (
          <form onSubmit={submit} className="space-y-6">
            <div>
              <input data-testid="reset-password" className={field} type="password" placeholder="New password" value={password} onChange={(e) => setPassword(e.target.value)} required minLength={8} />
              <PasswordStrength value={password} />
            </div>
            <input data-testid="reset-confirm" className={field} type="password" placeholder="Confirm password" value={confirm} onChange={(e) => setConfirm(e.target.value)} required minLength={8} />
            {error && <p data-testid="reset-error" className="text-sm text-red-400">{error}</p>}
            <button data-testid="reset-submit" disabled={loading} className="w-full py-4 bg-wine hover:bg-wine-hover transition-colors rounded-full text-sm uppercase tracking-[0.2em] disabled:opacity-50">
              {loading ? "..." : "Update password"}
            </button>
          </form>
        )}
        <Link to="/auth" className="mt-6 inline-block text-xs text-text-muted hover:text-text-main transition-colors">← Back to sign in</Link>
      </motion.div>
    </div>
  );
}
